import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ASSISTANT_LIMITS,
  ASSISTANT_MODELS,
  type AssistantConfigValues,
} from '@shared/assistant';
import { useAuth } from '../app/auth';
import { api, describeError } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Field,
  Input,
  Modal,
  Notice,
  PageHeader,
  Select,
  Skeleton,
  Textarea,
} from '../components/ui';

/**
 * AI 客服管理页。
 *
 * 分工：
 *   * 三枚开关在「平台开关」页统一管理（本页只读展示 + 跳转），
 *   * 提示词、模型参数、知识库与官网同步在这里维护。
 * 官网同步条目内容只读，避免运营编辑后又被下一次 cron 覆盖。
 */

interface AssistantConfig extends AssistantConfigValues {
  siteSyncedAt: string | null;
  updatedAt: string | null;
  updatedBy: string | null;
}

interface KnowledgeItem {
  id: string;
  source: 'manual' | 'site';
  sourceRef: string | null;
  title: string;
  content: string;
  keywords: string[];
  enabled: boolean;
  sortOrder: number;
  updatedAt: string;
  updatedBy: string;
}

interface Counts {
  total: number;
  manual: number;
  site: number;
  enabled: number;
}

interface FlagItem {
  key: string;
  label: string;
  enabled: boolean;
}

interface PageData {
  config: AssistantConfig;
  counts: Counts;
  items: KnowledgeItem[];
  flags: FlagItem[];
}

interface FormState {
  greeting: string;
  systemPrompt: string;
  model: string;
  maxTokens: string;
  temperature: string;
  suggestedQuestions: string;
}

function toForm(config: AssistantConfig): FormState {
  return {
    greeting: config.greeting,
    systemPrompt: config.systemPrompt,
    model: config.model,
    maxTokens: String(config.maxTokens),
    temperature: String(config.temperature),
    suggestedQuestions: config.suggestedQuestions.join('\n'),
  };
}

function formatTime(value: string | null): string {
  if (value === null) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('zh-CN', { hour12: false });
}

const SOURCE_LABEL: Record<KnowledgeItem['source'], string> = {
  manual: '手工',
  site: '官网同步',
};

export function AssistantPage() {
  const { can } = useAuth();
  const canManage = can('settings.manage');

  const [version, setVersion] = useState(0);
  const [form, setForm] = useState<FormState | null>(null);
  const [savingConfig, setSavingConfig] = useState(false);
  const [configError, setConfigError] = useState<string | null>(null);
  const [configSaved, setConfigSaved] = useState<string | null>(null);

  const [query, setQuery] = useState('');
  const [source, setSource] = useState<'all' | 'manual' | 'site'>('all');
  const [editor, setEditor] = useState<{ mode: 'create' | 'edit'; item?: KnowledgeItem } | null>(null);
  const [editorForm, setEditorForm] = useState({ title: '', content: '', keywords: '' });
  const [editorSaving, setEditorSaving] = useState(false);
  const [editorError, setEditorError] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<KnowledgeItem | null>(null);
  const [rowBusy, setRowBusy] = useState<string | null>(null);

  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState<string | null>(null);
  const [syncError, setSyncError] = useState<string | null>(null);

  const state = useAsync<PageData>(async () => {
    const [cfg, kb, flags] = await Promise.all([
      api.get<{ config: AssistantConfig; counts: Counts }>('/api/assistant/config'),
      api.get<{ items: KnowledgeItem[]; counts: Counts }>('/api/assistant/knowledge'),
      api.get<{ flags: FlagItem[] }>('/api/settings/flags'),
    ]);
    return {
      config: cfg.config,
      counts: kb.counts,
      items: kb.items,
      flags: flags.flags.filter((flag) => flag.key.startsWith('assistant.')),
    };
  }, [version]);

  useEffect(() => {
    if (state.data && form === null) setForm(toForm(state.data.config));
  }, [state.data, form]);

  const items = (state.data?.items ?? []).filter((item) => {
    if (source !== 'all' && item.source !== source) return false;
    const keyword = query.trim().toLowerCase();
    if (keyword === '') return true;
    return (
      item.title.toLowerCase().includes(keyword) ||
      item.content.toLowerCase().includes(keyword) ||
      item.keywords.some((tag) => tag.toLowerCase().includes(keyword))
    );
  });

  const masterEnabled = state.data?.flags.find((flag) => flag.key === 'assistant.enabled')?.enabled ?? false;

  async function saveConfig() {
    if (!form) return;
    setSavingConfig(true);
    setConfigError(null);
    setConfigSaved(null);
    try {
      const result = await api.patch<{ config: AssistantConfig }>('/api/assistant/config', {
        greeting: form.greeting,
        systemPrompt: form.systemPrompt,
        model: form.model,
        maxTokens: Number(form.maxTokens),
        temperature: Number(form.temperature),
        suggestedQuestions: form.suggestedQuestions
          .split('\n')
          .map((line) => line.trim())
          .filter(Boolean),
      });
      setForm(toForm(result.config));
      setConfigSaved('提示词与模型参数已保存，最多 30 秒内全站生效');
      setVersion((value) => value + 1);
    } catch (caught) {
      setConfigError(describeError(caught));
    } finally {
      setSavingConfig(false);
    }
  }

  function openCreate() {
    setEditor({ mode: 'create' });
    setEditorForm({ title: '', content: '', keywords: '' });
    setEditorError(null);
  }

  function openEdit(item: KnowledgeItem) {
    setEditor({ mode: 'edit', item });
    setEditorForm({
      title: item.title,
      content: item.content,
      keywords: item.keywords.join(', '),
    });
    setEditorError(null);
  }

  async function saveKnowledge() {
    if (!editor) return;
    setEditorSaving(true);
    setEditorError(null);
    try {
      const body = {
        title: editorForm.title,
        content: editorForm.content,
        keywords: editorForm.keywords
          .split(/[\n,，、]/)
          .map((value) => value.trim())
          .filter(Boolean),
      };
      if (editor.mode === 'create') {
        await api.post('/api/assistant/knowledge', body);
      } else if (editor.item) {
        await api.patch(`/api/assistant/knowledge/${encodeURIComponent(editor.item.id)}`, body);
      }
      setEditor(null);
      setVersion((value) => value + 1);
    } catch (caught) {
      setEditorError(describeError(caught));
    } finally {
      setEditorSaving(false);
    }
  }

  async function toggleKnowledge(item: KnowledgeItem, enabled: boolean) {
    setRowBusy(item.id);
    try {
      await api.patch(`/api/assistant/knowledge/${encodeURIComponent(item.id)}`, { enabled });
      setVersion((value) => value + 1);
    } catch (caught) {
      setConfigError(describeError(caught));
    } finally {
      setRowBusy(null);
    }
  }

  async function removeKnowledge(item: KnowledgeItem) {
    setRowBusy(item.id);
    try {
      await api.delete(`/api/assistant/knowledge/${encodeURIComponent(item.id)}`);
      setPendingDelete(null);
      setVersion((value) => value + 1);
    } catch (caught) {
      setConfigError(describeError(caught));
    } finally {
      setRowBusy(null);
    }
  }

  async function syncSite() {
    setSyncing(true);
    setSyncError(null);
    setSyncMessage(null);
    try {
      const result = await api.post<{
        synced: number;
        removed: number;
        at: string;
        error?: string;
      }>('/api/assistant/knowledge/sync');
      if (result.error) {
        setSyncError(result.error);
      } else {
        setSyncMessage(
          `已同步 ${result.synced} 条官网内容，移除 ${result.removed} 条过期条目（${formatTime(result.at)}）`,
        );
      }
      setVersion((value) => value + 1);
    } catch (caught) {
      setSyncError(describeError(caught));
    } finally {
      setSyncing(false);
    }
  }

  return (
    <div className="space-y-5">
      <PageHeader
        title="AI 客服"
        description="维护官网与控制台的智能客服：提示词、模型参数与知识库。开关在「平台开关」页统一控制。"
        action={
          <Button onClick={() => setVersion((value) => value + 1)} loading={state.loading}>
            刷新
          </Button>
        }
      />

      {!canManage ? (
        <Notice tone="info">
          当前角色只能查看 AI 客服配置。修改提示词、知识库或执行同步需要「调整平台开关」权限。
        </Notice>
      ) : null}

      {configError ? <Notice tone="danger">{configError}</Notice> : null}
      {configSaved ? <Notice tone="ok">{configSaved}</Notice> : null}
      {state.error ? <Notice tone="danger">{describeError(state.error)}</Notice> : null}

      {state.loading && state.data === null ? (
        <Skeleton className="h-64 w-full" />
      ) : state.data ? (
        <>
          <Card>
            <CardHeader
              title="全站控制器"
              hint="总开关关闭时，官网与控制台入口都会消失；单端开关只影响对应入口。"
              action={
                <Link to="/settings" className="text-[13px] text-brand hover:underline">
                  去平台开关调整 →
                </Link>
              }
            />
            <div className="flex flex-wrap items-center gap-3">
              {state.data.flags.map((flag) => (
                <span key={flag.key} className="flex items-center gap-2 rounded-md bg-canvas px-3 py-2">
                  <span className="text-[13px] text-fg">{flag.label}</span>
                  <Badge tone={flag.enabled ? 'ok' : 'warn'}>{flag.enabled ? '已开启' : '已关闭'}</Badge>
                </span>
              ))}
              {!masterEnabled ? (
                <span className="text-xs text-fg-muted">总开关当前关闭，用户看不到任何 AI 客服入口。</span>
              ) : null}
            </div>
            <p className="mt-3 text-xs text-fg-subtle">
              知识库共 {state.data.counts.total} 条（手工 {state.data.counts.manual} · 官网同步{' '}
              {state.data.counts.site}），其中 {state.data.counts.enabled} 条参与检索。
            </p>
          </Card>

          <Card>
            <CardHeader
              title="提示词与模型"
              hint="提示词会约束回答边界；修改后最多 30 秒内生效，主动失效时接近即时。"
              action={
                <Button variant="primary" onClick={saveConfig} loading={savingConfig} disabled={!canManage || form === null}>
                  保存
                </Button>
              }
            />
            {form ? (
              <div className="space-y-4">
                <Field label="欢迎语" htmlFor="assistant-greeting" hint={`最多 ${ASSISTANT_LIMITS.greetingMax} 字`}>
                  <Input
                    id="assistant-greeting"
                    value={form.greeting}
                    onChange={(value) => setForm({ ...form, greeting: value })}
                    maxLength={ASSISTANT_LIMITS.greetingMax}
                  />
                </Field>

                <Field
                  label="系统提示词"
                  htmlFor="assistant-prompt"
                  hint={`最多 ${ASSISTANT_LIMITS.systemPromptMax} 字。知识库内容会自动拼接在提示词之后。`}
                >
                  <Textarea
                    id="assistant-prompt"
                    value={form.systemPrompt}
                    onChange={(value) => setForm({ ...form, systemPrompt: value })}
                    rows={10}
                    maxLength={ASSISTANT_LIMITS.systemPromptMax}
                  />
                </Field>

                <Field
                  label="推荐问题"
                  htmlFor="assistant-suggestions"
                  hint={`每行一条，最多 ${ASSISTANT_LIMITS.suggestionsMax} 条，单条不超过 ${ASSISTANT_LIMITS.suggestionMax} 字`}
                >
                  <Textarea
                    id="assistant-suggestions"
                    value={form.suggestedQuestions}
                    onChange={(value) => setForm({ ...form, suggestedQuestions: value })}
                    rows={4}
                  />
                </Field>

                <div className="grid gap-4 sm:grid-cols-3">
                  <Field label="模型" htmlFor="assistant-model">
                    <Select
                      id="assistant-model"
                      value={form.model}
                      onChange={(value) => setForm({ ...form, model: value })}
                      options={ASSISTANT_MODELS.map((model) => ({ value: model.id, label: model.label }))}
                    />
                  </Field>
                  <Field label="最大输出 token" htmlFor="assistant-max-tokens">
                    <Input
                      id="assistant-max-tokens"
                      type="number"
                      value={form.maxTokens}
                      onChange={(value) => setForm({ ...form, maxTokens: value })}
                    />
                  </Field>
                  <Field label="温度（0–1）" htmlFor="assistant-temperature" hint="越低越稳定">
                    <Input
                      id="assistant-temperature"
                      type="number"
                      value={form.temperature}
                      onChange={(value) => setForm({ ...form, temperature: value })}
                    />
                  </Field>
                </div>

                <p className="text-xs text-fg-subtle">
                  最近保存：{formatTime(state.data.config.updatedAt)} · {state.data.config.updatedBy ?? '—'}
                </p>
              </div>
            ) : null}
          </Card>

          <Card>
            <CardHeader
              title="知识库"
              hint="手工条目可编辑；官网同步条目内容只读，可单独停用或调整排序。"
              action={
                <div className="flex flex-wrap items-center gap-2">
                  <div className="w-48">
                    <Input value={query} onChange={setQuery} placeholder="搜索标题或内容" />
                  </div>
                  <div className="w-32">
                    <Select
                      value={source}
                      onChange={(value) => setSource(value)}
                      options={[
                        { value: 'all', label: '全部来源' },
                        { value: 'manual', label: '手工' },
                        { value: 'site', label: '官网同步' },
                      ]}
                    />
                  </div>
                  <Button variant="primary" onClick={openCreate} disabled={!canManage}>
                    新增条目
                  </Button>
                </div>
              }
            />

            {items.length === 0 ? (
              <p className="py-8 text-center text-[13px] text-fg-muted">没有匹配的知识条目。</p>
            ) : (
              <ul className="divide-y divide-line">
                {items.map((item) => (
                  <li key={item.id} className="flex items-start justify-between gap-4 py-3">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-[13px] font-medium text-fg">{item.title}</span>
                        <Badge tone={item.source === 'site' ? 'brand' : 'neutral'}>{SOURCE_LABEL[item.source]}</Badge>
                        {!item.enabled ? <Badge tone="warn">已停用</Badge> : null}
                      </div>
                      <p className="mt-1 line-clamp-2 text-xs text-fg-muted">{item.content}</p>
                      {item.keywords.length > 0 ? (
                        <p className="mt-1 text-[11px] text-fg-subtle">关键词：{item.keywords.join(' · ')}</p>
                      ) : null}
                      <p className="mt-1 text-[11px] text-fg-subtle">
                        更新：{formatTime(item.updatedAt)} · {item.updatedBy}
                      </p>
                    </div>
                    <div className="flex shrink-0 items-center gap-3">
                      <label className="flex items-center gap-1.5 text-[12px] text-fg-muted">
                        <input
                          type="checkbox"
                          className="h-4 w-4 accent-[#1366ec]"
                          checked={item.enabled}
                          disabled={!canManage || rowBusy === item.id}
                          onChange={(event) => void toggleKnowledge(item, event.target.checked)}
                        />
                        启用
                      </label>
                      <Button
                        size="sm"
                        onClick={() => openEdit(item)}
                        disabled={!canManage || item.source === 'site'}
                      >
                        编辑
                      </Button>
                      <Button
                        size="sm"
                        variant="danger"
                        onClick={() => setPendingDelete(item)}
                        disabled={!canManage || item.source === 'site' || rowBusy === item.id}
                      >
                        删除
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <Card>
            <CardHeader
              title="官网内容同步"
              hint="从官网构建产物 /agent/index.json 抓取页面、产品与新闻，覆盖「官网同步」条目。每天自动同步一次。"
              action={
                <Button variant="primary" onClick={syncSite} loading={syncing} disabled={!canManage}>
                  立即同步
                </Button>
              }
            />
            <p className="text-xs text-fg-muted">
              上次同步：{formatTime(state.data.config.siteSyncedAt)}
            </p>
            {syncMessage ? (
              <div className="mt-3">
                <Notice tone="ok">{syncMessage}</Notice>
              </div>
            ) : null}
            {syncError ? (
              <div className="mt-3">
                <Notice tone="danger">{syncError}</Notice>
              </div>
            ) : null}
          </Card>
        </>
      ) : null}

      <Modal
        open={editor !== null}
        title={editor?.mode === 'edit' ? '编辑知识条目' : '新增知识条目'}
        description="条目会按关键词检索后拼接进系统提示词。内容越具体，回答越稳定。"
        onClose={() => setEditor(null)}
        footer={
          <>
            <Button onClick={() => setEditor(null)}>取消</Button>
            <Button variant="primary" onClick={saveKnowledge} loading={editorSaving}>
              保存
            </Button>
          </>
        }
      >
        {editorError ? (
          <div className="mb-3">
            <Notice tone="danger">{editorError}</Notice>
          </div>
        ) : null}
        <div className="space-y-4">
          <Field label="标题" htmlFor="kb-title">
            <Input
              id="kb-title"
              value={editorForm.title}
              onChange={(value) => setEditorForm({ ...editorForm, title: value })}
              maxLength={ASSISTANT_LIMITS.knowledgeTitleMax}
            />
          </Field>
          <Field label="内容" htmlFor="kb-content" hint={`最多 ${ASSISTANT_LIMITS.knowledgeContentMax} 字`}>
            <Textarea
              id="kb-content"
              value={editorForm.content}
              onChange={(value) => setEditorForm({ ...editorForm, content: value })}
              rows={10}
              maxLength={ASSISTANT_LIMITS.knowledgeContentMax}
            />
          </Field>
          <Field label="关键词" htmlFor="kb-keywords" hint="用逗号、顿号或换行分隔，最多 12 个">
            <Input
              id="kb-keywords"
              value={editorForm.keywords}
              onChange={(value) => setEditorForm({ ...editorForm, keywords: value })}
            />
          </Field>
        </div>
      </Modal>

      <Modal
        open={pendingDelete !== null}
        title="确认删除知识条目？"
        description="删除后无法恢复。"
        onClose={() => setPendingDelete(null)}
        footer={
          <>
            <Button onClick={() => setPendingDelete(null)}>取消</Button>
            <Button
              variant="danger"
              loading={pendingDelete !== null && rowBusy === pendingDelete.id}
              onClick={() => {
                if (pendingDelete) void removeKnowledge(pendingDelete);
              }}
            >
              删除
            </Button>
          </>
        }
      >
        <p className="text-[13px] text-fg">{pendingDelete?.title}</p>
      </Modal>
    </div>
  );
}
